import { FC } from 'react';

import { Stack, Typography, useTheme } from '@mui/material';
import { PickersRangeCalendarHeaderProps } from '@mui/x-date-pickers-pro';

import dayjs, { Dayjs } from 'dayjs';

import AppButton from '@/shared/ui/AppButton';

const NAV_BUTTON_SX = {
  minWidth: 0,
  minHeight: '27px',
  padding: '4px 8px',
};

const ChevronLeftIcon: FC<{ fill: string }> = ({ fill }) => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
    <path
      d="M9.64645 3.64645C9.84171 3.45118 10.1582 3.45118 10.3535 3.64645C10.5487 3.84171 10.5487 4.15824 10.3535 4.35348L6.70699 7.99996L10.3535 11.6464C10.5487 11.8417 10.5487 12.1582 10.3535 12.3535C10.1582 12.5487 9.84171 12.5487 9.64645 12.3535L5.64645 8.35348C5.45118 8.15822 5.45118 7.84171 5.64645 7.64645L9.64645 3.64645Z"
      fill={fill}
    />
  </svg>
);

const ChevronRightIcon: FC<{ fill: string }> = ({ fill }) => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
    <path
      d="M10.5 7.8333C10.5 7.9659 10.4473 8.09304 10.3535 8.18681L7.02018 11.5201C6.82492 11.7154 6.50841 11.7154 6.31315 11.5201C6.11789 11.3249 6.11789 11.0084 6.31315 10.8131L9.29297 7.8333L6.31315 4.85348C6.11789 4.65822 6.11789 4.34171 6.31315 4.14645C6.50841 3.95118 6.82492 3.95118 7.02018 4.14645L10.3535 7.47978L10.416 7.55595C10.4704 7.63748 10.5 7.73386 10.5 7.8333Z"
      fill={fill}
    />
  </svg>
);

const CalendarDateRangePickerHeader: FC<PickersRangeCalendarHeaderProps<Dayjs>> = ({
  currentMonth,
  month,
  onMonthChange,
  minDate,
  maxDate,
  disablePast,
  disableFuture,
  calendars,
  monthIndex,
}) => {
  const theme = useTheme();
  const effectiveMinDate = minDate ?? (disablePast ? dayjs() : undefined);
  const effectiveMaxDate = maxDate ?? (disableFuture ? dayjs() : undefined);
  const isPreviousDisabled = !!effectiveMinDate && month.isSameOrBefore(effectiveMinDate, 'month');
  const isNextDisabled = !!effectiveMaxDate && month.isSameOrAfter(effectiveMaxDate, 'month');
  const showPrevious = monthIndex === 0;
  const showNext = monthIndex === calendars - 1;

  return (
    <Stack
      direction="row"
      alignItems="center"
      justifyContent="space-between"
      sx={{ width: '100%', marginBottom: '12px' }}
    >
      <AppButton
        variant="primary"
        size="small"
        sx={{ ...NAV_BUTTON_SX, visibility: showPrevious ? 'visible' : 'hidden' }}
        disabled={isPreviousDisabled}
        onClick={() => onMonthChange(currentMonth.subtract(1, 'month'), 'right')}
        aria-label="Previous month"
      >
        <ChevronLeftIcon fill={theme.palette.accentExtension.sidemenu} />
      </AppButton>
      <Typography
        sx={{
          fontSize: '16px',
          fontWeight: 500,
          lineHeight: 1,
          color: (theme) => theme.palette.grey[900],
        }}
      >
        {month.format('MMMM YYYY')}
      </Typography>
      <AppButton
        variant="primary"
        size="small"
        sx={{ ...NAV_BUTTON_SX, visibility: showNext ? 'visible' : 'hidden' }}
        disabled={isNextDisabled}
        onClick={() => onMonthChange(currentMonth.add(1, 'month'), 'left')}
        aria-label="Next month"
      >
        <ChevronRightIcon fill={theme.palette.accentExtension.sidemenu} />
      </AppButton>
    </Stack>
  );
};

export default CalendarDateRangePickerHeader;
